import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Instagram, Download, Loader2 } from "lucide-react";

/**
 * Renders a 1080×1080 Instagram-ready square combining:
 *   - The property's primary photo (top ~65%)
 *   - A dark bottom band with the Guest Smell wordmark + freshness score
 *   - Property name + city subline
 *
 * Uses <canvas> so hosts can download a real PNG with `download` attribute.
 */
export function InstagramSquare({ property, tier = "premium" }) {
  const canvasRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const S = 1080;
      canvas.width = S; canvas.height = S;

      // 1. background solid navy while photo loads
      ctx.fillStyle = "#0A1128";
      ctx.fillRect(0, 0, S, S);

      // 2. property photo (top 700px, cover)
      const img = new Image();
      img.crossOrigin = "anonymous";
      const src = property.photos?.[0] || "https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=1600";
      // Force through images.unsplash.com CDN (it supports CORS) via appending param
      img.src = src.includes("?") ? src : src + "?crossorigin=anonymous";

      await new Promise((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => resolve(); // continue with placeholder if photo blocks CORS
        setTimeout(resolve, 4000);
      });

      const photoH = 700;
      if (img.complete && img.naturalWidth > 0) {
        // object-fit cover math
        const aspect = img.naturalWidth / img.naturalHeight;
        let dw = S, dh = S / aspect;
        if (dh < photoH) { dh = photoH; dw = photoH * aspect; }
        const dx = (S - dw) / 2;
        const dy = (photoH - dh) / 2;
        ctx.drawImage(img, dx, dy, dw, dh);
        // Vignette
        const grad = ctx.createLinearGradient(0, photoH - 200, 0, photoH);
        grad.addColorStop(0, "rgba(10,17,40,0)");
        grad.addColorStop(1, "rgba(10,17,40,0.6)");
        ctx.fillStyle = grad;
        ctx.fillRect(0, photoH - 200, S, 200);
      }

      // 3. Bottom band (380px tall, navy)
      ctx.fillStyle = "#0A1128";
      ctx.fillRect(0, photoH, S, S - photoH);

      // 4. Eyebrow (gold)
      ctx.fillStyle = "#D4AF37";
      ctx.font = "600 22px 'Outfit', Arial, sans-serif";
      ctx.letterSpacing = "8px";
      ctx.textBaseline = "top";
      ctx.fillText("GUEST SMELL " + (tier === "premium" ? "PREMIUM" : "STANDARD") + " CERTIFIED", 60, photoH + 50);

      // 5. Property name (large serif white)
      ctx.fillStyle = "#FFFFFF";
      ctx.font = "500 54px 'Playfair Display', Georgia, serif";
      const name = (property.name || "Certified Stay").slice(0, 30);
      ctx.fillText(name, 60, photoH + 100);

      // 6. City / country (slate)
      ctx.fillStyle = "#94A3B8";
      ctx.font = "400 26px 'Outfit', Arial, sans-serif";
      const sub = `${property.city || ""}${property.country ? ", " + property.country : ""}`;
      ctx.fillText(sub, 60, photoH + 175);

      // 7. Freshness score badge (right side circle)
      const cx = S - 180;
      const cy = photoH + 175;
      ctx.beginPath();
      ctx.arc(cx, cy, 105, 0, Math.PI * 2);
      ctx.fillStyle = "#10B981";
      ctx.fill();
      ctx.beginPath();
      ctx.arc(cx, cy, 90, 0, Math.PI * 2);
      ctx.fillStyle = "#0A1128";
      ctx.fill();
      // score
      ctx.fillStyle = "#FFFFFF";
      ctx.font = "600 68px 'Playfair Display', Georgia, serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(property.freshness_score ?? "—"), cx, cy - 8);
      ctx.font = "500 14px 'Outfit', Arial, sans-serif";
      ctx.fillStyle = "#94A3B8";
      ctx.fillText("/ 100 FRESH", cx, cy + 40);

      // 8. Footer tagline
      ctx.textAlign = "left";
      ctx.fillStyle = "#64748B";
      ctx.font = "500 20px 'Outfit', Arial, sans-serif";
      ctx.fillText("guestsmell.com  ·  See It. Smell It. Book It.™", 60, S - 60);

      setReady(true);
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `guest-smell-${(property.name || "stay").toLowerCase().replace(/\s+/g, "-")}-instagram.png`;
    a.click();
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-200 p-6" data-testid="instagram-square-kit">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.24em] uppercase text-emerald-700 font-semibold">Social Kit</div>
          <h3 className="mt-2 font-serif text-2xl text-[#0A1128]">Instagram-ready square</h3>
          <p className="mt-1 text-sm text-slate-600 max-w-md">
            One-tap download a branded 1080×1080 image of this stay with the freshness score baked in. Post to Instagram / Facebook / your listing gallery.
          </p>
        </div>
        <div className="w-11 h-11 rounded-2xl bg-emerald-50 flex items-center justify-center shrink-0">
          <Instagram className="w-5 h-5 text-emerald-600" />
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        {!ready ? (
          <Button
            onClick={generate}
            disabled={busy}
            className="rounded-full bg-[#0A1128] hover:bg-[#1A2A3A] text-white"
            data-testid="insta-generate"
          >
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Rendering…</> : <><Instagram className="w-4 h-4 mr-2" /> Generate square</>}
          </Button>
        ) : (
          <Button
            onClick={download}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="insta-download"
          >
            <Download className="w-4 h-4 mr-2" /> Download 1080×1080 PNG
          </Button>
        )}
        {ready && (
          <Button
            variant="outline"
            onClick={() => { setReady(false); generate(); }}
            className="rounded-full border-slate-300"
            data-testid="insta-regenerate"
          >
            Regenerate
          </Button>
        )}
      </div>

      {/* Preview canvas — always mounted so ref exists, hidden until ready */}
      <div className={`mt-6 ${ready ? "" : "hidden"}`}>
        <canvas
          ref={canvasRef}
          className="w-full max-w-sm rounded-2xl border border-slate-200 shadow-md"
          data-testid="insta-canvas"
        />
      </div>
    </div>
  );
}

export default InstagramSquare;
